import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaSearch, FaFilter, FaSpinner, FaEye, FaBriefcase, FaCalendarAlt, FaBuilding, FaClock } from 'react-icons/fa';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const API = `${API_CONFIG2.apiUrl}candidat/getOffresWithCandidatures.php`;

const OffrePoster = ({ candidatId }) => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // Chargement des données
  const loadData = () => {
    setLoading(true);
    axios
      .get(API, {
        params: {
          candidatId,
          page,
          limit: 10,
          search,
          status: filterStatus, // filtre par statut de candidature
        },
      })
      .then((res) => {
        setData(res.data.data);
        setPages(res.data.pages);
      })
      .catch((err) => {
        console.error('Erreur de chargement', err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [page, search, filterStatus]);

  // Réinitialiser la page lors d'une recherche ou d'un filtre
  useEffect(() => {
    setPage(1);
  }, [search, filterStatus]);

  // Fonction pour obtenir la classe CSS du badge
  const badgeClass = (statut) => {
    const s = statut?.toLowerCase() || '';
    if (s === 'acceptée' || s === 'accepte') return 'badge-success';
    if (s === 'refusée' || s === 'refusee') return 'badge-danger';
    if (s === 'en attente' || s === 'enattente') return 'badge-warning';
    return 'badge-secondary';
  };

  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – OFFRES POSTULÉES
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .offres-wrapper {
          background: var(--bg-gradient);
          padding: 30px;
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
          min-height: 100vh;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.8rem;
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }

        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .header-title {
          font-size: 1.8rem;
          font-weight: 700;
          background: linear-gradient(135deg, #e0e7ff, #a5b4fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 0.5rem;
        }

        .search-filter-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
        }

        .search-wrapper {
          flex: 1;
          min-width: 200px;
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 0 16px;
          transition: all 0.3s;
        }

        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }

        .search-wrapper input {
          background: transparent;
          border: none;
          padding: 12px 0 12px 10px;
          color: var(--text-primary);
          outline: none;
          flex: 1;
          font-size: 0.95rem;
        }

        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .filter-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 12px 20px;
          color: var(--text-primary);
          outline: none;
          font-size: 0.9rem;
          min-width: 150px;
          cursor: pointer;
          transition: border-color 0.3s;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394a3b8' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 16px center;
        }

        .filter-select:focus {
          border-color: var(--accent-blue);
        }

        .filter-select option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .table-container {
          overflow-x: auto;
        }

        .offre-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.95rem;
        }

        .offre-table th {
          padding: 16px 12px;
          text-align: left;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.06);
          font-weight: 600;
        }

        .offre-table td {
          padding: 16px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
          color: var(--text-secondary);
        }

        .offre-table tr {
          transition: background 0.15s;
        }

        .offre-table tr:hover td {
          background: rgba(255,255,255,0.03);
        }

        .offre-title {
          font-weight: 600;
          color: #fff;
          margin-bottom: 4px;
        }

        .offre-desc {
          font-size: 0.85rem;
          color: var(--text-muted);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .badge {
          display: inline-block;
          padding: 4px 14px;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .badge-success {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }
        .badge-danger {
          background: rgba(239, 68, 68, 0.15);
          color: #f87171;
        }
        .badge-warning {
          background: rgba(251, 191, 36, 0.15);
          color: #fbbf24;
        }
        .badge-secondary {
          background: rgba(255, 255, 255, 0.04);
          color: var(--text-secondary);
        }

        .pagination-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          margin-top: 24px;
        }

        .pagination-btn {
          padding: 8px 20px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .pagination-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }

        .pagination-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .pagination-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }

        .pagination-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }

        .page-indicator {
          color: var(--text-secondary);
          font-size: 0.9rem;
          font-weight: 500;
        }

        .loader-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 60px 0;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(59, 130, 246, 0.08);
          border-top-color: var(--accent-blue);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: var(--text-secondary);
        }

        .empty-state svg {
          font-size: 3rem;
          margin-bottom: 16px;
          opacity: 0.3;
        }

        @media (max-width: 768px) {
          .offres-wrapper { padding: 16px; }
          .search-filter-bar { flex-direction: column; align-items: stretch; }
          .filter-select { width: 100%; }
          .offre-table td, .offre-table th { padding: 12px 8px; font-size: 0.85rem; }
          .pagination-wrapper { flex-wrap: wrap; }
        }

        .fade-in {
          animation: fadeUp 0.4s ease forwards;
          opacity: 0;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .text-muted { color: var(--text-muted); }
        .fw-bold { font-weight: 600; }
      `}</style>

      <div className="offres-wrapper">
        <div className="glass-card">
          <h2 className="header-title">
            <FaBriefcase className="me-2" style={{ color: 'var(--accent-blue)' }} />
            Offres postulées
          </h2>

          {/* Barre de recherche et filtre */}
          <div className="search-filter-bar">
            <div className="search-wrapper">
              <FaSearch style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Rechercher par titre, service..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="Acceptée">Acceptée</option>
              <option value="Refusée">Refusée</option>
              <option value="En attente">En attente</option>
            </select>
          </div>
        </div>

        {/* Tableau des offres */}
        <div className="glass-card">
          {loading ? (
            <div className="loader-container">
              <div className="spinner"></div>
            </div>
          ) : data.length === 0 ? (
            <div className="empty-state">
              <FaBriefcase />
              <p>Aucune offre trouvée pour ce candidat.</p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="offre-table">
                  <thead>
                    <tr>
                      <th>Offre</th>
                      <th>Service</th>
                      <th>Publication</th>
                      <th>Postulation</th>
                     
                     
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, idx) => (
                      <tr key={item.candidature_id || idx} className="fade-in" style={{ animationDelay: `${idx * 50}ms` }}>
                        <td>
                          <div className="offre-title">{item.titre || 'Sans titre'}</div>
                         
                        </td>
                        <td>
                          <FaBuilding className="me-1" style={{ color: 'var(--accent-blue)', opacity: 0.5 }} />
                          {item.service || '—'}
                        </td>
                        <td>
                          <FaCalendarAlt className="me-1" style={{ color: 'var(--accent-blue)', opacity: 0.5 }} />
                          {item.datePublication || '—'}
                        </td>
                        <td>
                          <FaClock className="me-1" style={{ color: 'var(--accent-blue)', opacity: 0.5 }} />
                          {item.datePostulation || '—'}
                        </td>
                       
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination-wrapper">
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Précédent
                </button>
                <span className="page-indicator">
                  {page} / {pages}
                </span>
                <button
                  className="pagination-btn primary"
                  onClick={() => setPage(page + 1)}
                  disabled={page === pages}
                >
                  Suivant
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OffrePoster;